import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useWaUnread } from "../../hooks/useWaUnread";
import { useApprovalsCount } from "../../hooks/useApprovalsCount";
import { usePermissions } from "../../hooks/usePermissions";
import { buildMenu, itemActive, type MenuItem } from "./menuItems";
import "../../styles/bottom-nav.css";

interface Props {
  user: any;
  onLogout: () => void;
}

interface Tab {
  key: string;
  icon: string;
  label: string;
  /** Пункты раздела; если пункт один и раздел «прямой», сразу переходим на страницу */
  items: MenuItem[];
  direct?: boolean;
  badge?: number;
}

/**
 * Нижнее меню на телефоне: 4–5 разделов. Нажатие на раздел раскрывает его страницы над панелью,
 * повторное нажатие (или нажатие мимо) закрывает список и возвращает к прежнему экрану.
 */
export default function BottomNav({ user, onLogout }: Props) {
  const { pathname } = useLocation();
  const perms = usePermissions();
  const waUnread = useWaUnread();
  const approvals = useApprovalsCount(user?.role === "ADMIN");
  const [openKey, setOpenKey] = useState<string | null>(null);

  // после перехода на другую страницу список закрываем
  useEffect(() => { setOpenKey(null); }, [pathname]);

  const tabs = useMemo<Tab[]>(() => {
    const { top, groups, system } = buildMenu(user, perms, { wa: waUnread, approvals });
    const all = [...top, ...groups.flatMap((g) => g.items), ...system];
    const pick = (paths: string[]) =>
      paths
        .map((p) => all.find((i) => i.to === p))
        .filter((i): i is MenuItem => !!i && i.show);
    const sum = (items: MenuItem[]) => items.reduce((s, i) => s + (i.badge || 0), 0);

    const sales = pick(["/deals", "/work-orders", "/clients", "/companies", "/whatsapp", "/deals/finished"]);
    const money = pick(["/pos", "/pos-report", "/promotions", "/cash-debts", "/credit", "/reports"]);
    const stock = pick(["/products", "/warehouse", "/keys", "/al-catalog", "/print"]);
    const more: MenuItem[] = [
      ...pick(["/approvals", "/integrations", "/settings"]),
      { to: "/profile", icon: "👤", label: "Личный кабинет", show: true },
    ];

    const list: Tab[] = [];
    if (user?.role === "ADMIN") {
      const dash = pick(["/dashboard"]);
      if (dash.length) list.push({ key: "home", icon: "🏠", label: "Главная", items: dash, direct: true });
    }
    if (sales.length) list.push({ key: "sales", icon: "💼", label: "Продажи", items: sales, badge: sum(sales) });
    if (money.length) list.push({ key: "money", icon: "🧾", label: "Деньги", items: money });
    if (stock.length) list.push({ key: "stock", icon: "📦", label: "Склад", items: stock });
    list.push({ key: "more", icon: "⚙️", label: "Ещё", items: more, badge: approvals });
    return list;
  }, [user, perms, waUnread, approvals]);

  const open = tabs.find((t) => t.key === openKey) || null;

  const onTab = (t: Tab) => {
    if (t.direct) {
      setOpenKey(null);
      return;
    }
    setOpenKey((k) => (k === t.key ? null : t.key));
  };

  return (
    <>
      {open && <div className="bnav-backdrop" onClick={() => setOpenKey(null)} />}
      {open && (
        <div className="bnav-sheet" role="menu">
          <div className="bnav-sheet-title">{open.label}</div>
          <div className="bnav-grid">
            {open.items.map((it) => (
              <Link key={it.to} to={it.to} className={`bnav-tile ${itemActive(it, pathname) ? "active" : ""}`} role="menuitem">
                <span className="bnav-tile-icon">{it.icon}</span>
                <span className="bnav-tile-label">{it.label}</span>
                {it.badge ? <span className="bnav-badge">{it.badge}</span> : null}
              </Link>
            ))}
            {open.key === "more" && (
              <button type="button" className="bnav-tile bnav-logout" onClick={onLogout}>
                <span className="bnav-tile-icon">🚪</span>
                <span className="bnav-tile-label">Выход</span>
              </button>
            )}
          </div>
        </div>
      )}
      <nav className="bnav" aria-label="Основное меню">
        {tabs.map((t) => {
          const active = t.items.some((i) => itemActive(i, pathname));
          const content = (
            <>
              <span className="bnav-icon">
                {t.icon}
                {t.badge ? <span className="bnav-badge">{t.badge}</span> : null}
              </span>
              <span className="bnav-label">{t.label}</span>
            </>
          );
          const cls = `bnav-tab ${active ? "active" : ""} ${openKey === t.key ? "open" : ""}`;
          return t.direct ? (
            <Link key={t.key} to={t.items[0].to} className={cls} onClick={() => onTab(t)}>
              {content}
            </Link>
          ) : (
            <button key={t.key} type="button" className={cls} onClick={() => onTab(t)} aria-expanded={openKey === t.key}>
              {content}
            </button>
          );
        })}
      </nav>
    </>
  );
}
